import { useEffect } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Hero from "@/components/landing/Hero";
import ValueCards from "@/components/landing/ValueCards";
import WhatYouSee from "@/components/landing/WhatYouSee";
import ContentStrip from "@/components/landing/ContentStrip";
import PartnerFlow from "@/components/landing/PartnerFlow";
import PlatformIntro from "@/components/landing/PlatformIntro";
import BeforeYouGo from "@/components/landing/BeforeYouGo";
import Footer from "@/components/landing/Footer";
import StickyBar from "@/components/landing/StickyBar";
import LanguageSwitcher from "@/components/landing/LanguageSwitcher";
import Header from "@/components/landing/Header";
import LegalPage from "@/components/landing/LegalPage";
import { useLanguage } from "@/i18n/LanguageContext";
import { pageTitles } from "@/i18n/translations";
import { legalPages } from "@/i18n/legal";
import { DEFAULT_LANG } from "@/i18n/detectLanguage";

const LEGAL_PATHS = {
  "/privacy": "privacy",
  "/terms": "terms",
  "/affiliate-disclosure": "disclosure",
  "/responsible-gaming": "responsible",
};

function TitleSync() {
  const { lang } = useLanguage();
  const { pathname } = useLocation();
  useEffect(() => {
    const site = pageTitles[lang] || pageTitles[DEFAULT_LANG];
    const doc = LEGAL_PATHS[pathname];
    if (doc) {
      const lp = legalPages[lang] || legalPages[DEFAULT_LANG];
      document.title = `${lp[doc].title} | ${site}`;
    } else {
      document.title = site;
    }
  }, [lang, pathname]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function BridgeLine() {
  const { t } = useLanguage();
  return (
    <p
      data-testid="bridge-line"
      className="mx-auto w-full max-w-md px-8 pb-1 pt-2 text-center text-[11px] leading-relaxed text-slate-400"
    >
      {t("bridgeLine")}
    </p>
  );
}

function Landing() {
  return (
    <>
      <Header />
      <Hero />
      <div aria-hidden="true" className="mx-auto w-full max-w-md px-6 py-1">
        <div className="h-1 rounded-full bg-[linear-gradient(90deg,transparent,#1663E6_25%,#F2B705_50%,#1663E6_75%,transparent)]" />
      </div>
      <main className="mx-auto w-full max-w-md">
        <ValueCards />
        <BridgeLine />
        <ContentStrip />
        <WhatYouSee />
        <PartnerFlow />
        <PlatformIntro />
        <BeforeYouGo />
        <Footer />
      </main>
      <StickyBar />
    </>
  );
}

function GlobalChrome() {
  const { pathname } = useLocation();
  return pathname === "/" ? null : <LanguageSwitcher />;
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.05, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="app-shell" data-testid="app-shell">
      <BrowserRouter>
        <TitleSync />
        <GlobalChrome />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/privacy" element={<LegalPage doc="privacy" />} />
          <Route path="/terms" element={<LegalPage doc="terms" />} />
          <Route path="/affiliate-disclosure" element={<LegalPage doc="disclosure" />} />
          <Route path="/responsible-gaming" element={<LegalPage doc="responsible" />} />
          <Route path="*" element={<Landing />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}
